/**
 * Чистая часть карточки замка: отбор, подписи и правило звонка.
 *
 * Модуль ЧИСТЫЙ, как lib/twaplock.ts и lib/signals.ts, и по той же причине:
 * компонентных тестов в проекте нет, поэтому всё, о чём можно ошибиться, обязано
 * жить в .ts.
 *
 * # Чего здесь НЕТ и не должно быть
 *
 * Ни расчёта цены, ни размера, ни прибыли. Всё это считает трейдер по ЖИВОМУ
 * стакану и присылает готовым: у браузера стакана этого рынка обычно нет вовсе —
 * карточка видна с любой страницы, а подписка на стакан идёт только по открытому
 * рынку. Посчитай мы что-нибудь здесь, на экране оказалось бы число, которое ни
 * с чем не сверено.
 */

import type { LockPlan, LockSignal, LockState } from "../api/types";
import { parse, SCALE } from "./money";
import { formatDollars } from "./tape";

/**
 * Доллары с ЦЕНТАМИ — здесь, а не общим formatDollars.
 *
 * Тот округляет до десятой доли доллара, и для ленты это верно: там сравнивают
 * сотни и тысячи. Здесь величины другие. Тейкерская комиссия на $50 по 0.99 —
 * три с половиной цента, и «$0.0» на кнопке читается как «комиссии нет»: ровно
 * та подмена, ради предотвращения которой комиссия и считается. Вся прибыль
 * сделки у единицы — цент на share, и десятая доля доллара съедает её тоже.
 */
function cents(micro: number): string {
  const sign = micro < 0 ? "−" : "";
  return `${sign}$${(Math.abs(micro) / SCALE).toFixed(2)}`;
}

/**
 * Оговорка о происхождении числа. Постоянная строка карточки.
 *
 * Та же, что на панели «Сверка индекса», и по той же причине: всё, что говорит
 * карточка, — это утверждение о числе, которое раздаёт Polymarket, а не о
 * подписанном отчёте Chainlink. Без неё аларм читается как ГАРАНТИЯ исхода, и
 * оператор жмёт кнопку, думая, что покупает определённость.
 */
export const SOURCE_NOTE =
  "пересчёт раздачи Polymarket; с подписанным отчётом Chainlink не сверялось";

/**
 * Та же оговорка на карточке — в четверть длины.
 *
 * Полная строка занимала ОТДЕЛЬНУЮ третью строку карточки, то есть треть её
 * высоты, и повторялась на каждой: карточка замка выходила втрое выше карточки
 * кита, стоящей рядом в том же макете. В подсказку оговорку убирать нельзя —
 * она про то, чем является всё остальное, — но названа она может быть коротко.
 * Полная формулировка осталась подсказкой этого же места.
 */
export const SOURCE_SHORT = "по Polymarket, не Chainlink";

/** Карточка ЖИВА, пока ждёт решения и не сгорела. Только у таких есть кнопки. */
export function isLive(signal: LockSignal, now: Date): boolean {
  if (signal.outcome !== "alerted") return false;
  const expires = Date.parse(signal.expires_at);
  return Number.isFinite(expires) && expires > now.getTime();
}

/**
 * Есть ли по карточке ЖИВАЯ кнопка. Карточки без неё на экран не идут вовсе.
 *
 * # Почему нечего показывать без кнопки
 *
 * Карточка не сообщение, а предложение сделки: состояние замка видно и на панели
 * «Сверка индекса», а карточка добавляет к нему ровно одно — возможность нажать.
 * Без единой годной кнопки от неё остаётся строка отказа (иногда — с
 * идентификатором токена в семьдесят восемь цифр), и стоит эта строка в СКВОЗНОЙ
 * полосе поверх всех страниц, вытесняя те карточки, по которым действовать можно.
 *
 * # Что при этом пропадает с экрана
 *
 * Причина отказа: «стакан не ведётся» (трейдер не подписан на рынок), «рынок
 * выключен в риск-лимитах», «расписание комиссии незнакомо», «сделка не
 * приносит». Первые две постоянны на всё окно, вторые две зависят от стакана и
 * проходят сами. Ни одна из них не требует действия ПО КАРТОЧКЕ — все чинятся
 * там, где заданы, — поэтому молчание здесь честнее строки, на которую нельзя
 * ответить.
 *
 * # Мигание
 *
 * Карточка, чья прибыль колеблется около нуля, будет то появляться, то пропадать
 * — планы пересчитываются раз в секунду. Звонок при этом второй раз не даёт:
 * ключ прозвучавшего помнится по `id:state` и переживает исчезновение карточки
 * (см. chimeKey). То есть дребезг остаётся визуальным и не будит оператора.
 */
export function actionable(signal: LockSignal): boolean {
  return [signal.maker, signal.taker].some((p) => p !== undefined && planUsable(p));
}

/** Сколько осталось до сгорания карточки, в миллисекундах. */
export function remainingMs(signal: LockSignal, now: Date): number {
  const expires = Date.parse(signal.expires_at);
  if (!Number.isFinite(expires)) return 0;
  return Math.max(0, expires - now.getTime());
}

/**
 * Живые карточки. Сначала определённый исход, потом почти закрытый замок.
 *
 * Порядок содержательный, а не по времени: settled — это деньги, которые можно
 * взять прямо сейчас, safe — ставка с преимуществом. Когда на экране обе, первая
 * важнее, и решать, какую читать раньше, оператор не должен.
 */
export function liveSignals(signals: LockSignal[], now: Date): LockSignal[] {
  return signals
    .filter((s) => isLive(s, now) && actionable(s))
    .sort((a, b) => {
      if (a.state !== b.state) return a.state === "settled" ? -1 : 1;
      return Date.parse(b.detected_at) - Date.parse(a.detected_at);
    });
}

/**
 * Ключ прозвучавшего — идентификатор И СОСТОЯНИЕ, а не один идентификатор.
 *
 * Переход safe → settled случается в ТОЙ ЖЕ записи с тем же id: это одно окно, о
 * котором мы узнали больше. Но для оператора это НОВОЕ решение — исход перестал
 * быть вероятным и стал определённым, — и прозвенеть оно обязано второй раз.
 * Звони мы по одному id, самое важное состояние приходило бы молча.
 *
 * Побочно это гасит дребезг: карточка, отозванная и поднятая заново тем же
 * состоянием, второго звонка не даёт — ключ у неё тот же.
 */
export function chimeKey(signal: LockSignal): string {
  return `${signal.id}:${signal.state}`;
}

/** Какие живые карточки ещё не звучали. */
export function unannounced(
  live: readonly LockSignal[],
  announced: ReadonlySet<string>,
): string[] {
  return live.map(chimeKey).filter((key) => !announced.has(key));
}

/**
 * Состояние словами — коротко, для заголовка карточки.
 *
 * Полную формулировку присылает сервер (label): она объясняет, ПОЧЕМУ состояние
 * такое. Здесь — имя состояния, которое читается за долю секунды и по которому
 * карточка опознаётся в ряду.
 */
export function stateTitle(state: LockState): string {
  switch (state) {
    case "settled":
      return "Исход определён";
    case "safe":
      return "Замок почти закрыт";
    case "none":
      return "Исход открыт";
  }
}

/**
 * Состояние в одно-два слова — то, что стоит НА карточке рядом со значком.
 *
 * Значок отвечает на «что это за карточка» (замок, а не кит) и цветом со формой
 * — на «какое состояние»; слово называет состояние прямо, потому что здесь оно,
 * в отличие от подписи «Кит поставил» на соседней карточке, у каждой карточки
 * своё. Полная формулировка сервера — в подсказке значка: она объясняет ПОЧЕМУ,
 * и на это в строке места нет.
 */
export function stateShort(state: LockState): string {
  switch (state) {
    case "settled":
      return "определён";
    case "safe":
      return "почти закрыт";
    case "none":
      return "открыт";
  }
}

/** Цвет состояния. Определённый исход — тревожный акцент, почти закрытый — янтарь. */
export function stateColor(state: LockState): string {
  switch (state) {
    case "settled":
      return "var(--color-buy)";
    case "safe":
      return "var(--color-warn)";
    case "none":
      return "var(--color-muted)";
  }
}

/**
 * Что обещает кнопка — одной строкой: цена, размер, деньги.
 *
 * Прочерк вместо числа НИКОГДА не молчит: пустое место на месте цены читается
 * как ноль, а ноль здесь — это цена.
 */
export function planLabel(plan: LockPlan): string {
  if (plan.reason) return plan.reason;
  const price = plan.price ?? "—";
  const size = plan.size ?? "—";
  const quote = plan.quote ? formatDollars(plan.quote) : "—";
  return `${price} × ${size} share = ${quote}`;
}

/**
 * Прибыль плана словами. null — считать её не из чего, и кнопка обязана сказать
 * это, а не показать пустоту.
 *
 * Молчит она ровно в одном случае: расписание комиссии рынка незнакомо. Показать
 * тогда «грязную» прибыль значило бы завысить её на величину, которую биржа
 * спишет, — то есть соврать в ту сторону, в которую врать дороже всего.
 */
export function pnlLabel(plan: LockPlan): string | null {
  if (!plan.fee_known) return null;
  const pnl = parse(plan.pnl);
  if (pnl === null) return null;
  // Плюс ставится явно: у прибыли знак — половина смысла, и «$1.45» без него
  // читается одинаково и как заработок, и как убыток.
  return `${pnl > 0 ? "+" : ""}${cents(pnl)}`;
}

/** Тон прибыли: цвет обещает знак, поэтому у нуля и у неизвестного его нет. */
export function pnlTone(plan: LockPlan): "buy" | "sell" | "muted" {
  if (!plan.fee_known) return "muted";
  const pnl = parse(plan.pnl);
  if (pnl === null || pnl === 0) return "muted";
  return pnl > 0 ? "buy" : "sell";
}

/**
 * Годна ли кнопка к нажатию.
 *
 * Неприбыльный план гасится ЗДЕСЬ, а не только на сервере, и это не дублирование
 * защиты: кнопка, которая выглядит живой и отвечает отказом, приучает не читать
 * отказы. Настоящий рубеж всё равно на сервере — он пересчитывает план из
 * живого стакана в момент команды.
 */
export function planUsable(plan: LockPlan): boolean {
  if (plan.reason || !plan.size) return false;
  if (!plan.fee_known) return false;
  const pnl = parse(plan.pnl);
  return pnl !== null && pnl > 0;
}

/**
 * Почему кнопка погашена. Пусто — она живая.
 *
 * Причин три, и они требуют разного: причина от сервера («в стакане нет
 * объёма»), незнакомое расписание («посчитать нечем») и убыточность («стакан
 * уехал»). Общей фразой не отделаться — оператор пошёл бы чинить не то.
 */
export function planProblem(plan: LockPlan): string {
  if (plan.reason) return plan.reason;
  if (!plan.size) return "размер не собран";
  if (!plan.fee_known) {
    return "расписание комиссии рынка незнакомо — прибыль не посчитать";
  }
  const pnl = parse(plan.pnl);
  if (pnl === null) return "прибыль не посчитана";
  if (pnl <= 0) return "сделка не приносит: стакан дороже погашения";
  return "";
}

/**
 * Всё, что кнопка больше не показывает строкой, — одной подсказкой.
 *
 * Цена, размер, комиссия и глубина стакана стояли ВТОРОЙ строкой под каждой
 * кнопкой и удваивали высоту карточки. Читают их не в момент решения: решение
 * принимается по прибыли, которая осталась на кнопке цветом. Причина отказа,
 * наоборот, вытесняет всё — выключенная кнопка обязана объяснить себя.
 *
 * Показывать эту подсказку должна ОБЁРТКА кнопки, а не сама кнопка: выключенной
 * кнопке браузер событий мыши не даёт, и `title` на ней не показывается вовсе —
 * ровно там, где причина и нужна.
 */
export function planTitle(plan: LockPlan): string {
  const problem = planProblem(plan);
  if (problem !== "") return problem;
  const fee = feeLabel(plan);
  const depth = depthNote(plan);
  return `${planLabel(plan)}${fee ? `, ${fee}` : ""}${depth ? ` (${depth})` : ""}`;
}

/**
 * Комиссия словами. Пусто у мейкера с нулём — там говорить не о чем.
 *
 * Ноль у мейкера — это ЗНАНИЕ, а не отсутствие числа, и оно уже сказано ролью на
 * кнопке. А вот прочерк при незнакомом расписании молчать не имеет права: его
 * называет planProblem.
 */
export function feeLabel(plan: LockPlan): string {
  if (!plan.fee_known) return "";
  const fee = parse(plan.fee);
  if (fee === null || fee === 0) return "";
  return `комиссия ${cents(fee)}`;
}

/**
 * Насколько глубоко пришлось лезть в стакан, и выбрана ли сумма.
 *
 * Пусто, когда сказать нечего: один уровень и выбранный бюджет — это норма, а
 * подпись о норме приучает не читать это место. Ровно то же правило, по которому
 * молчит покрытие окна на панели сверки.
 */
export function depthNote(plan: LockPlan): string {
  const parts: string[] = [];
  if ((plan.levels ?? 0) > 1) parts.push(`${plan.levels} уровня стакана`);
  // Неполный набор — не отказ, а свойство рынка в конце окна, и оператору его
  // надо видеть: иначе сумма на кнопке обещала бы объём, которого в стакане нет.
  if (plan.partial) parts.push("стакан кончился раньше денег");
  return parts.join(", ");
}

/**
 * Запас над страйком со стороной. Пусто — считать не из чего.
 *
 * Отвечает на вопрос «почему сторона именно эта»: запас и есть прогноз итога
 * против страйка. Число в долларах ИНДЕКСА, а не в деньгах сделки, — поэтому со
 * знаком «выше/ниже», а не с плюсом и минусом: перепутать их на карточке, где
 * рядом стоит прибыль в долларах, стоило бы дорого.
 */
export function cushionLabel(signal: LockSignal): string {
  const cushion = parse(signal.cushion);
  if (cushion === null || cushion === 0) return "";
  const where = cushion > 0 ? "выше" : "ниже";
  return `${where} страйка на ${(Math.abs(cushion) / 1_000_000).toFixed(2)} $`;
}

/**
 * Тот же запас на карточке: направление — стрелкой, величина — числом.
 *
 * Слова «выше страйка на» занимали в строке больше, чем само число, и говорили
 * то же самое, что стрелка. Знака доллара здесь НЕТ намеренно: величина в
 * долларах ИНДЕКСА, а рядом на кнопке стоит прибыль в долларах сделки, и общий
 * значок валюты у двух разных величин — приглашение их перепутать. Единицы
 * называет подпись «страйк» рядом и полная подсказка (cushionLabel).
 */
export function cushionShort(signal: LockSignal): string {
  const cushion = parse(signal.cushion);
  if (cushion === null || cushion === 0) return "";
  return `${cushion > 0 ? "▲" : "▼"}${(Math.abs(cushion) / 1_000_000).toFixed(2)}`;
}

/**
 * Что стало с карточкой — для тех состояний, где кнопок уже нет.
 *
 * Показывается недолго и только чтобы нажатие не выглядело провалившимся в
 * пустоту: карточка уходит с экрана сама, когда сервер пришлёт её закрытой.
 */
export function outcomeNote(signal: LockSignal): string {
  switch (signal.outcome) {
    case "alerted":
      return "";
    case "placed":
      return signal.taken_kind === "maker"
        ? "заявка выставлена — ждём исполнения"
        : "ордер отправлен";
    case "skipped":
      return "пропущено вами";
    case "expired":
      return "сгорело: решение не принято";
    case "revoked":
      // Самое важное из четырёх: состояние ОТКАТИЛОСЬ, то есть цена пошла
      // обратно к страйку. Это не «мы передумали показывать», а «сделка
      // перестала быть безопасной».
      return "отозвано: цена вернулась, замок разжался";
  }
}
